// Pooled inbox SP2 (W1). The message-list reducer bodies SP1 wrote in
// threadSlice, lifted out unchanged so the advisor's open thread
// (inboxSlice.open) runs exactly the same rules as the client's thread:
// newest-first storage, _id dedupe, optimistic replacement by tempId with the
// R51 echo dedupe (both orderings), failed-send marking, older-page append.
//
// Each function mutates `sub` — an object holding { messages, nextBefore,
// sendErrors } — in place. Inside createSlice that object is an Immer draft;
// in tests it is a plain object. Nothing here does I/O or reads the clock.

export const RECEIVED = {
  REPLACED: "replaced",
  DUPLICATE: "duplicate",
  INSERTED: "inserted",
};

export function applyMessagesLoaded(
  sub,
  { messages, nextBefore, prepend = false },
) {
  if (prepend) {
    // An older page: append only what we do not already hold.
    const older = messages.filter(
      (m) => !sub.messages.some((x) => x._id === m._id),
    );
    sub.messages = [...sub.messages, ...older];
  } else {
    // The newest page is server truth; keep only optimistic locals the page
    // has not caught up with (they carry a tempId and no server _id yet).
    // A failed send stays `pending` (applyMessageSendFailed does not clear
    // it), so it survives here too along with its retry affordance.
    const pending = sub.messages.filter(
      (m) => m.pending && !messages.some((x) => x._id === m._id),
    );
    sub.messages = [...pending, ...messages];
  }
  sub.nextBefore = nextBefore ?? null;
}

export function applyMessageReceived(sub, payload) {
  const byTemp = payload.tempId
    ? sub.messages.findIndex((m) => m.tempId === payload.tempId)
    : -1;
  if (byTemp >= 0) {
    // The server broadcasts with io.to(room).emit(...) — which includes the
    // sender — and emits before it writes the 201, so the sender normally sees
    // the socket echo of its own send FIRST. That echo carries no tempId and
    // the optimistic bubble still holds `_id === tempId`, so the _id check
    // below cannot match it and the echo is unshifted as a second entry.
    // Remove any OTHER entry already holding this _id before assigning (R51).
    const dupe = sub.messages.findIndex(
      (m, i) => i !== byTemp && m._id === payload._id,
    );
    if (dupe >= 0) sub.messages.splice(dupe, 1);
    const at = sub.messages.findIndex((m) => m.tempId === payload.tempId);
    sub.messages[at] = { ...payload, pending: undefined };
    delete sub.sendErrors[payload.tempId];
    return RECEIVED.REPLACED;
  }
  if (sub.messages.some((m) => m._id === payload._id))
    return RECEIVED.DUPLICATE;
  sub.messages.unshift(payload);
  return RECEIVED.INSERTED;
}

export function applyMessageSendFailed(sub, { tempId, text }) {
  sub.sendErrors[tempId] = text;
  const i = sub.messages.findIndex((m) => m.tempId === tempId);
  if (i >= 0) sub.messages[i].failed = true;
}

// Removes the failed bubble and its error in one step. The caller must read
// the failed message's text *before* dispatching this, or the text is gone.
export function applyMessageSendRetried(sub, tempId) {
  delete sub.sendErrors[tempId];
  sub.messages = sub.messages.filter((m) => m.tempId !== tempId);
}
